import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { motion } from 'framer-motion';
import FriendlyRobot from '../components/FriendlyRobot';

// --- Constants ---
const MODEL_PATH = '/models/face_landmarker.task'; // Ensure this model exists in public/models
const MIN_FACE_BOX_SIZE = 0.3;
const MAX_FACE_BOX_SIZE = 0.6;
const OPTIMAL_MIN_FACE_BOX_SIZE = 0.40; // Stricter minimum for optimal score
const OPTIMAL_MAX_FACE_BOX_SIZE = 0.55; // Stricter maximum for optimal score
const MIN_BRIGHTNESS = 50; // Adjusted brightness threshold
const MAX_BRIGHTNESS_SCORE_THRESHOLD = 180; // Upper limit for optimal brightness score mapping
const TARGET_FACE_OVAL_RATIO = 1.6; 
const OVAL_SCALING_FACTOR = 0.4; // Reduced oval size (adjust as needed)
const OVAL_COLOR_DEFAULT = 'rgba(255, 255, 255, 0.5)';
const OVAL_COLOR_GOOD = 'rgba(0, 255, 0, 0.7)';
const OVAL_COLOR_BAD = 'rgba(255, 0, 0, 0.7)';
// Indices for face contour landmarks (example set, might need refinement)
const FACE_CONTOUR_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152,
  148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109
];

// Simple Gauge Component
const QualityGauge = ({ score }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  let strokeColor = '#ef4444'; // red-500
  if (score > 70) strokeColor = '#22c55e'; // green-500
  else if (score > 40) strokeColor = '#f59e0b'; // amber-500

  return (
    <div className="relative w-32 h-32">
      <svg className="w-full h-full" viewBox="0 0 120 120">
        {/* Background Circle */}
        <circle
          className="text-gray-300"
          strokeWidth="10"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx="60"
          cy="60"
        />
        {/* Progress Circle */}
        <circle
          className="transition-stroke-dashoffset duration-300 ease-linear"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke={strokeColor} // Use dynamic color
          fill="transparent"
          r={radius}
          cx="60"
          cy="60"
          transform="rotate(-90 60 60)"
        />
        {/* Text */}
        <text 
          className="text-xl font-bold"
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill="currentColor"
        >
          {`${Math.round(score)}%`}
        </text>
      </svg>
    </div>
  );
};

export default function TestKYC() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null); // For temporary drawing/analysis
  const overlayCanvasRef = useRef(null); // Canvas for drawing overlays
  const faceLandmarkerRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const [message, setMessage] = useState('در حال بارگذاری مدل و دسترسی به دوربین...');
  const [detailedMessage, setDetailedMessage] = useState(''); // For secondary messages
  const [isReadyToCapture, setIsReadyToCapture] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [qualityScore, setQualityScore] = useState(0);
  const [ovalStatus, setOvalStatus] = useState('default');
  const [individualScores, setIndividualScores] = useState({ // State for individual scores
      presence: 0,
      distance: 0,
      angle: 0, // Combined angle score for now
      brightness: 0,
  });
  const [videoResolution, setVideoResolution] = useState(''); // State for resolution

  useEffect(() => {
    let stream = null;
    let videoElement = videoRef.current; // Capture ref value
    const localMaxAngleThreshold = 15; // Define locally within useEffect scope

    const setupMediaPipe = async () => {
      try {
        setMessage('در حال بارگذاری مدل...');
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
        );
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_PATH,
            delegate: "GPU"
          },
          outputFaceBlendshapes: false, // Disable if not needed
          outputFacialTransformationMatrixes: true, // Enable matrix output
          runningMode: "VIDEO",
          numFaces: 1
        });

        setMessage('درخواست دسترسی به دوربین...');
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480 }, // Specific resolution can help consistency
          audio: false
        });
        if (videoElement) {
          videoElement.srcObject = stream;
          videoElement.addEventListener('loadeddata', () => {
            console.log("Video data loaded. Starting prediction loop.");
            setVideoResolution(`${videoElement.videoWidth}x${videoElement.videoHeight}`); // Set resolution state
            predictWebcam(); // Start prediction loop
          });
          setIsLoading(false); // Loading finished
        }
      } catch (error) {
        console.error("Failed to initialize MediaPipe or webcam:", error);
        setMessage(`خطا: ${error.message}. لطفا دسترسی دوربین را بررسی کنید.`);
        setIsLoading(false);
      }
    };

    const predictWebcam = () => {
      const videoElement = videoRef.current;
      if (!faceLandmarkerRef.current || !videoElement || videoElement.readyState < 4) {
        // console.log("Waiting for video or landmarker...");
        animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
        return;
      }

      const startTimeMs = performance.now();
      const results = faceLandmarkerRef.current.detectForVideo(videoElement, startTimeMs);

      let totalScore = 0;
      let primaryMsg = ""; // Initialize empty
      let secondaryMsg = "";
      let isReady = false;
      let ovalStatus = 'bad'; // Default to bad
      const scores = { presence: 0, distance: 0, angle: 0, brightness: 0 };
      const angleDetails = { roll: 99, pitch: 99, yaw: 99 }; 

      // --- Check 1: Face Presence & Matrix ---
      if (!results.faceLandmarks || results.faceLandmarks.length === 0 || !results.facialTransformationMatrixes || results.facialTransformationMatrixes.length === 0) {
          primaryMsg = "چهره‌ای شناسایی نشد. لطفا صورت خود را کامل در کادر بیضی قرار دهید.";
          // Reset all scores and states
          setQualityScore(0);
          setMessage(primaryMsg);
          setDetailedMessage("");
          setIsReadyToCapture(false);
          setOvalStatus('bad');
          setIndividualScores(scores);
          animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
          return; // Exit early if no face
      }

      // --- Face Detected: Proceed with checks ---
      scores.presence = 100;
      primaryMsg = "چهره شناسایی شد.";
      const landmarks = results.faceLandmarks[0];
      const matrix = results.facialTransformationMatrixes[0].data;
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      // --- Check 2: Face Position within Oval & Minimum Size ---
      let positionOK = false;
      scores.distance = 0; // This score now represents both fit and size
      ovalStatus = 'bad';

      // Calculate oval parameters
      const ovalCenterX = videoWidth / 2;
      const ovalCenterY = videoHeight / 2;
      const ovalRadiusX = (videoWidth * OVAL_SCALING_FACTOR) / 2;
      const ovalRadiusY = (videoWidth * OVAL_SCALING_FACTOR * TARGET_FACE_OVAL_RATIO) / 2;
      const radiusXSquared = ovalRadiusX * ovalRadiusX;
      const radiusYSquared = ovalRadiusY * ovalRadiusY;

      let allPointsInside = true;
      if (radiusXSquared > 0 && radiusYSquared > 0) {
          for (const index of FACE_CONTOUR_INDICES) {
              if (index >= landmarks.length) continue; // Skip if index out of bounds
              const point = landmarks[index];
              const px = point.x * videoWidth;
              const py = point.y * videoHeight;
              
              // Check ellipse equation
              const dx = px - ovalCenterX;
              const dy = py - ovalCenterY;
              if (((dx * dx) / radiusXSquared) + ((dy * dy) / radiusYSquared) > 1.05) {
                  allPointsInside = false;
                  break;
              }
          }
      } else {
          allPointsInside = false;
      }

      if (allPointsInside) {
          // Face fits the oval shape, now check minimum size
          const { xMin, xMax, yMin, yMax } = getBoundingBox(landmarks, videoWidth, videoHeight);
          const faceWidth = xMax - xMin;
          const faceHeight = yMax - yMin;
          const relativeWidth = faceWidth / videoWidth;
          const relativeHeight = faceHeight / videoHeight;
          const avgSize = (relativeWidth + relativeHeight) / 2;

          // Use OPTIMAL_MIN_FACE_BOX_SIZE as the absolute minimum required size when inside oval
          if (avgSize >= OPTIMAL_MIN_FACE_BOX_SIZE) { 
              positionOK = true;
              ovalStatus = 'good';
              primaryMsg = "موقعیت چهره مناسب است.";
              scores.distance = 100; 
          } else {
              // Inside oval but too small (too far according to new stricter threshold)
              positionOK = false;
              ovalStatus = 'bad';
              primaryMsg = "لطفا کمی نزدیک‌تر شوید.";
              // Score based on proximity to the *optimal* min size now
              scores.distance = Math.max(0, Math.round((avgSize / OPTIMAL_MIN_FACE_BOX_SIZE) * 69)); 
          }
      } else {
          // Face contour doesn't fit the oval
          positionOK = false;
          ovalStatus = 'bad';
          primaryMsg = "لطفا چهره خود را کامل در کادر بیضی قرار دهید.";
          scores.distance = 0;
      }

      // --- Early exit if position is not OK ---
      if (!positionOK) {
          setQualityScore(Math.round((scores.distance + scores.angle + scores.brightness) / 3));
          setMessage(primaryMsg);
          setDetailedMessage("");
          setIsReadyToCapture(false);
          setOvalStatus(ovalStatus);
          setIndividualScores(scores);
          animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
          return;
      }

      // --- Check 3: Angle (Roll, Pitch, Yaw) - ONLY if positionOK is true ---
      if (!positionOK) {
        console.warn("Reached angle check despite positionOK being false - check logic.");
        // ... (set state and return, similar to the block above)
        setMessage(primaryMsg);
        setDetailedMessage("");
        setIsReadyToCapture(false);
        setOvalStatus(ovalStatus);
        setIndividualScores(scores);
        animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
        return;
      }

      // --- Check 3: Angle (Roll, Pitch, Yaw) ---
      const { roll, pitch, yaw } = getEulerAnglesFromMatrix(matrix);
      angleDetails.roll = roll; angleDetails.pitch = pitch; angleDetails.yaw = yaw;
      const localMaxAngleThreshold = 15;
      const angleRollOK = Math.abs(roll) <= localMaxAngleThreshold;
      const anglePitchOK = Math.abs(pitch) <= localMaxAngleThreshold;
      const angleYawOK = Math.abs(yaw) <= localMaxAngleThreshold;
      const allAnglesOK = angleRollOK && anglePitchOK && angleYawOK;
      let angleScore = 0;
      if(angleRollOK) angleScore += 34;
      if(anglePitchOK) angleScore += 33;
      if(angleYawOK) angleScore += 33;
      scores.angle = Math.min(100, Math.round(angleScore));

      if (!allAnglesOK) {
          primaryMsg = "لطفا سر خود را صاف نگه دارید.";
          ovalStatus = 'bad'; // Angle is bad, oval should be red
          if (!angleRollOK) secondaryMsg = `کجی سر: ${roll.toFixed(1)}°`;
          else if (!anglePitchOK) secondaryMsg = `زاویه بالا/پایین: ${pitch.toFixed(1)}°`;
          else secondaryMsg = `چرخش سر: ${yaw.toFixed(1)}°`;
          // Exit after setting message if angle is wrong
          setQualityScore(Math.round((scores.distance + scores.angle + scores.brightness) / 3));
          setMessage(primaryMsg);
          setDetailedMessage(secondaryMsg);
          setIsReadyToCapture(false);
          setOvalStatus(ovalStatus);
          setIndividualScores(scores);
          animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
          return;
      } else {
           primaryMsg = "زاویه سر مناسب است."; // Update message if angles OK
           secondaryMsg = ""; // Clear secondary message
      }

      // --- Check 4: Brightness ---
      const brightnessValue = analyzeBrightness(videoElement);
      let brightOK = false;
      let brightnessScoreCalc = 0;
      if (brightnessValue < MIN_BRIGHTNESS) {
           brightnessScoreCalc = Math.max(0, Math.round((brightnessValue / MIN_BRIGHTNESS) * 70));
           primaryMsg = "محیط تاریک است. لطفا نور را بیشتر کنید.";
           ovalStatus = 'bad';
           secondaryMsg = "";
      } else {
          brightOK = true;
          if (brightnessValue > MAX_BRIGHTNESS_SCORE_THRESHOLD) {
               brightnessScoreCalc = Math.max(70, Math.round(100 - (brightnessValue - MAX_BRIGHTNESS_SCORE_THRESHOLD)/5 ));
          } else {
               brightnessScoreCalc = 100;
          }
          primaryMsg = "نور محیط مناسب است.";
          secondaryMsg = "";
      }
      scores.brightness = Math.max(0, Math.min(100, brightnessScoreCalc));
      
      if (!brightOK) {
           // Exit after setting message if brightness is wrong
          setQualityScore(Math.round((scores.distance + scores.angle + scores.brightness) / 3));
          setMessage(primaryMsg);
          setDetailedMessage(secondaryMsg);
          setIsReadyToCapture(false);
          setOvalStatus(ovalStatus);
          setIndividualScores(scores);
          animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
          return;
      }
      
      // --- All Checks Passed --- 
      totalScore = 100; // Explicitly set 100
      isReady = true;
      primaryMsg = "عالی! آماده ثبت تصویر.";
      secondaryMsg = "لطفا عینک خود را بردارید (اگر استفاده می‌کنید).";
      ovalStatus = 'good';
      // Also set individual scores to 100 if all checks passed
      scores.distance = 100; scores.angle = 100; scores.brightness = 100;

     // --- Final State Updates ---
     setQualityScore(totalScore); 
     setMessage(primaryMsg);
     setDetailedMessage(secondaryMsg);
     setIsReadyToCapture(isReady); // Use the isReady flag
     setOvalStatus(ovalStatus);
     setIndividualScores(scores);

     // Call predictWebcam again
     animationFrameIdRef.current = requestAnimationFrame(predictWebcam);
    };

    setupMediaPipe();

    // --- Cleanup Function (mostly unchanged, ensure state reset) ---
    return () => {
      cancelAnimationFrame(animationFrameIdRef.current);
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close(); // Release MediaPipe resources
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop()); // Stop webcam
      }
      if (videoElement) {
        videoElement.removeEventListener('loadeddata', predictWebcam); // Remove listener
      }
       setMessage('دوربین غیرفعال شد.');
       setIsReadyToCapture(false);
       const overlayCtx = overlayCanvasRef.current?.getContext('2d');
       if (overlayCtx) {
           overlayCtx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
       }
       setQualityScore(0);
       setOvalStatus('default'); // Reset oval status
       setIndividualScores({ presence: 0, distance: 0, angle: 0, brightness: 0 });
       setVideoResolution('');
    };
  }, []); // Keep empty dependency array

  // --- Helper Functions ---
  const getBoundingBox = (landmarks, width, height) => {
    let xMin = width, xMax = 0, yMin = height, yMax = 0;
    landmarks.forEach(point => {
      const x = point.x * width;
      const y = point.y * height;
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    });
    return { xMin, xMax, yMin, yMax };
  };

  // Very basic angle estimation based on eye levelness
  // Assumes specific landmark indices (these might vary slightly between models)
  // Left eye outer corner: 359, Right eye outer corner: 130 (Check official docs for exact indices)
  const estimateFaceAngle = (landmarks) => {
      const leftEye = landmarks[359]; // Example index
      const rightEye = landmarks[130]; // Example index
      if (!leftEye || !rightEye) return 90; // Return large angle if eyes not detected
      const deltaY = (leftEye.y - rightEye.y); // * videoHeight (normalized, no need for actual height)
      const deltaX = (leftEye.x - rightEye.x); // * videoWidth
      if (Math.abs(deltaX) < 1e-6) return 90; // Avoid division by zero if eyes are vertically aligned
      const angleRad = Math.atan2(deltaY, deltaX);
      return angleRad * (180 / Math.PI);
  };

  // Simple brightness analysis: draw frame to canvas and get average pixel value
  const analyzeBrightness = (video) => {
    if (!canvasRef.current || !video.videoWidth) return 128; // Default if not ready

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true }); // Important for performance
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
            // Basic grayscale conversion: average R, G, B
            const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
            sum += brightness;
        }
        return sum / (data.length / 4); // Average brightness
    } catch (e) {
        // SecurityError can happen if canvas/image is tainted (e.g., cross-origin)
        console.error("Brightness analysis failed:", e);
        return 128; // Return default on error
    }
  };

  const handleCapture = () => {
    if (!videoRef.current || !isReadyToCapture) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas'); // Create temporary canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    // Flip the image horizontally before drawing if the video feed is mirrored
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Reset the transformation to get the data URL correctly
    // Alternatively, don't flip the context, draw normally, and mention the output is mirrored.
    // Let's keep it simple for now and log the mirrored capture.
    // If non-mirrored needed, flip ctx back before get DataURL or flip video feed itself

    const imageDataUrl = canvas.toDataURL('image/png');
    console.log('Captured Image Data URL:', imageDataUrl);
    setMessage('تصویر با موفقیت در کنسول ثبت شد.');
    setIsReadyToCapture(false); // Prevent immediate recapture

    // Optional: Display the captured image
    // const img = document.createElement('img');
    // img.src = imageDataUrl;
    // document.body.appendChild(img); // Append somewhere in your UI
  };

  // --- Euler Angle Extraction (Requires matrix library or careful implementation) ---
  const getEulerAnglesFromMatrix = (matrixData) => {
    // Assumes matrixData is a Float32Array(16) in column-major order.
    // Assumes ZYX rotation order convention.

    // Column-major indices mapping to rotation matrix elements (top-left 3x3):
    // r11 = matrixData[0], r21 = matrixData[1], r31 = matrixData[2]
    // r12 = matrixData[4], r22 = matrixData[5], r32 = matrixData[6]
    // r13 = matrixData[8], r23 = matrixData[9], r33 = matrixData[10]

    let yaw, pitch, roll;
    const radToDeg = 180 / Math.PI;

    // Calculate Pitch (around Y')
    const sinPitch = -matrixData[2]; // -r31

    // Check for gimbal lock (pitch is +/- 90 degrees)
    if (Math.abs(sinPitch) >= 0.99999) {
        pitch = Math.sign(sinPitch) * (Math.PI / 2); // Set pitch to +/- 90deg
        
        // In gimbal lock, conventionally set roll to 0
        roll = 0;
        // Calculate yaw based on the remaining rotation
        yaw = Math.atan2(-matrixData[4], matrixData[0]); // atan2(-r12, r11) - Adjusted for gimbal lock
    } else {
        pitch = Math.asin(sinPitch);

        // Calculate Yaw (around Z)
        yaw = Math.atan2(matrixData[1], matrixData[0]); // atan2(r21, r11)

        // Calculate Roll (around X'')
        roll = Math.atan2(matrixData[6], matrixData[10]); // atan2(r32, r33)
    }

    // Convert to degrees
    // Note: Signs might need adjustment depending on the exact coordinate system definition
    //       and desired output convention (+ve right/up/clockwise etc.)
    return {
        roll: roll * radToDeg,
        pitch: pitch * radToDeg,
        yaw: yaw * radToDeg
    };
};
  
  // --- Updated Drawing function ---
  const drawOverlay = useCallback((status) => {
      const canvas = overlayCanvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video || !video.videoWidth) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw the guiding oval
      const ovalWidth = canvas.width * OVAL_SCALING_FACTOR;
      const ovalHeight = ovalWidth * TARGET_FACE_OVAL_RATIO; 
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      let strokeColor = OVAL_COLOR_DEFAULT;
      if (status === 'good') strokeColor = OVAL_COLOR_GOOD;
      else if (status === 'bad') strokeColor = OVAL_COLOR_BAD;
      
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, ovalWidth / 2, ovalHeight / 2, 0, 0, 2 * Math.PI);
      ctx.stroke();
      
      // Mirror the drawing context *only* for subsequent drawings if needed
      // If just drawing the oval, no mirroring of context is strictly necessary
      // ctx.save();
      // ctx.scale(-1, 1);
      // ctx.translate(-canvas.width, 0);
      // ... draw other things mirrored ...
      // ctx.restore();

  }, []); 

  // Effect to draw overlay when status changes
   useEffect(() => {
        drawOverlay(ovalStatus);
    }, [ovalStatus, drawOverlay, isLoading]); // Redraw when status or loading changes

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4 overflow-hidden">
       {/* Use a container to manage layout */} 
      <div className="flex flex-col items-center w-full max-w-lg mx-auto">
          
         <h1 className="text-2xl font-bold mb-4 text-center">تایید هویت با چهره</h1>
         <p className="text-sm text-gray-400 mb-6 text-center">لطفا چهره خود را در کادر بیضی شکل قرار دهید.</p>
         {/* Friendly Robot SVG */}
         <FriendlyRobot size={150} />

         {/* Video and Overlay Container */}
         <div className="relative w-full aspect-[4/3] max-w-md border-2 border-gray-700 rounded-lg overflow-hidden bg-black mb-6">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              style={{ transform: 'scaleX(-1)' }} // Mirror display
              onLoadedMetadata={() => {
                  // Attempt to draw initial overlay once video metadata is loaded
                  // This helps show the oval even before detection starts
                  drawOverlay(ovalStatus);
              }}
            ></video>
            {/* Overlay Canvas */}
            <canvas 
                ref={overlayCanvasRef} 
                className="absolute top-0 left-0 w-full h-full pointer-events-none" 
            />
            {/* Loading Indicator */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mr-2"></div>
                    در حال بارگذاری...
                </div>
            )}
             {/* Guidance Message Overlay */}
            {!isLoading && (message || detailedMessage) && (
                 <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 p-3 rounded-b-lg text-center z-10">
                    {message && <p className="text-sm font-semibold mb-1">{message}</p>}
                    {detailedMessage && <p className="text-xs text-amber-400">{detailedMessage}</p>}
                 </div>
             )}
         </div>

         {/* Gauge, Scores, and Button Section */}
         <div className="flex flex-col items-center space-y-4 w-full mt-6">
             {/* Quality Gauge */}
             {!isLoading && <QualityGauge score={qualityScore} />} 
             
             {/* Detailed Scores */}
              {!isLoading && (
                 <div className="text-xs text-gray-400 grid grid-cols-2 gap-x-4 gap-y-1 w-full max-w-sm text-center">
                     <span>وضوح تصویر: <span className="font-mono text-gray-300">{videoResolution || '-'}</span></span>
                     <span>روشنایی: <span className="font-mono text-gray-300">{individualScores.brightness.toFixed(0)}%</span></span>
                     <span>فاصله تا دوربین: <span className="font-mono text-gray-300">{individualScores.distance.toFixed(0)}%</span></span>
                     <span>زاویه سر (کل): <span className="font-mono text-gray-300">{individualScores.angle.toFixed(0)}%</span></span>
                     {/* Optional: Display individual angles if needed for debug/detail */}
                     {/* 
                     <span>Roll: {angleDetails.roll.toFixed(1)}°</span> 
                     <span>Pitch: {angleDetails.pitch.toFixed(1)}°</span> 
                     <span>Yaw: {angleDetails.yaw.toFixed(1)}°</span> 
                     */}
                 </div>
              )}
              
             {/* Capture Button */}
             <button
                onClick={handleCapture}
                disabled={!isReadyToCapture}
                className={`px-8 py-3 rounded-full font-semibold text-lg transition-all duration-300 w-full max-w-xs ${
                 isReadyToCapture
                    ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg transform hover:scale-105'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
             >
                {isReadyToCapture ? 'ثبت تصویر' : (isLoading ? 'صبر کنید...' : 'شرایط نامناسب')}
             </button>
         </div>
            
         {/* Hidden canvas for brightness analysis */}
         <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
          
       </div>
    </div>
  );
} 